import { logger } from "@/common/logger";
import { ActionContext } from "@/components/DatasetVersionHistory/common";
import { Trash01Icon } from "@/icons";
import { Button } from "@/kit/ui/button";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import { relayErrorMessage } from "@/utils/relay";
import { toast } from "@/utils/toast";
import { MouseEventHandler, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ConnectionHandler, graphql, useMutation } from "react-relay";
import { DeleteActionVersionHistoryMutation as DeleteActionVersionHistoryMutationType } from "./__generated__/DeleteActionVersionHistoryMutation.graphql";

const DeleteActionVersionHistoryMutation = graphql`
  mutation DeleteActionVersionHistoryMutation($id: ID!, $connections: [ID!]!) {
    deleteDatasetVersion(datasetVersionId: $id)
      @deleteEdge(connections: $connections)
  }
`;

export function DeleteAction() {
  const [open, setOpen] = useState(false);
  const { datasetId, version, id } = useContext(ActionContext);
  const [commitMutation, isMutationInFlight] =
    useMutation<DeleteActionVersionHistoryMutationType>(
      DeleteActionVersionHistoryMutation,
    );

  const doSubmit: MouseEventHandler = (event) => {
    event.preventDefault();
    commitMutation({
      variables: {
        id,
        connections: [
          ConnectionHandler.getConnectionID(
            datasetId,
            "ListVersionHistoryFragment_versions",
          ),
          ConnectionHandler.getConnectionID(
            datasetId,
            "DatasetVersionCreateButtonFragment_versionsInput",
          ),
        ],
      },
      onCompleted() {
        toast.success(
          <FormattedMessage
            defaultMessage="“{version}” deleted successfully."
            values={{ version }}
          />,
        );
      },
      onError(error) {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });
    setOpen(false);
  };

  return (
    <ResponsiveModal open={open} onOpenChange={setOpen}>
      <ResponsiveModalTrigger asChild>
        <Button
          variant="tertiary"
          hue="danger"
          size="md"
          className="flex flex-row items-center justify-start gap-2 w-full hover:bg-linear-to-r from-[#FFF1F0] to-[#FFF] rounded-none active:bg-none bg-none px-3 py-4"
          disabled={isMutationInFlight}
        >
          <Trash01Icon className="w-6 h-6" />
          <FormattedMessage defaultMessage="Delete version" />
        </Button>
      </ResponsiveModalTrigger>
      <ResponsiveModalContent className="sm:w-96 w-auto bg-linear-to-b from-[#FCEDED] from-[-16.39%] to-white to-[65.62%] ">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="bg-error-100 rounded-md p-2 w-fit">
            <Trash01Icon className="text-error-500 w-8 h-8" />
          </div>
          <ResponsiveModalTitle>
            <FormattedMessage defaultMessage="Delete dataset version" />
          </ResponsiveModalTitle>
          <ResponsiveModalDescription className="text-center w-10/12">
            <FormattedMessage
              defaultMessage="You're about to delete “{version}” and all its data. This action can’t be undone."
              values={{ version }}
            />
          </ResponsiveModalDescription>
          <div className="w-full flex flex-row gap-2 mt-4">
            <ResponsiveModalClose asChild>
              <Button
                variant="secondary"
                disabled={isMutationInFlight}
                className="w-full"
              >
                <FormattedMessage defaultMessage="Cancel" />
              </Button>
            </ResponsiveModalClose>
            <Button
              onClick={doSubmit}
              variant="primary"
              hue="danger"
              disabled={isMutationInFlight}
              className="w-full"
            >
              <Trash01Icon className="w-5 h-5" />
              <FormattedMessage defaultMessage="Delete" />
            </Button>
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
